/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Carries the env-merged i18n (baseline + i18n_admin overrides) into the
 * server render so LanguageContext can seed its initial state with admin
 * copy instead of the static baseline. This is what makes the prerendered
 * HTML body show the correct env-specific text on first paint.
 *
 * On the client, the analogous seed comes from `window.__INITIAL_I18N__`
 * which the prerender step injects into the HTML. The bake passes both the
 * same object, holding the page's own language only.
 */

import React, { createContext, useContext } from 'react';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type SSRTranslations = Partial<Record<'nl' | 'en', any>>;

const SSRTranslationsContext = createContext<SSRTranslations | null>(null);

export const SSRTranslationsProvider: React.FC<{
  value: SSRTranslations | null;
  children: React.ReactNode;
}> = ({ value, children }) => (
  <SSRTranslationsContext.Provider value={value}>{children}</SSRTranslationsContext.Provider>
);

export function useSSRTranslations(): SSRTranslations | null {
  return useContext(SSRTranslationsContext);
}
